/**
 * useQuickCreate — shared quick-create logic for mobile FAB + desktop "+Neu".
 *
 * 4 actions: note, task, scouting, poi.
 * Each action reuses existing endpoints/forms.
 * GPS default location via useGeolocation.
 *
 * UX-P2: FAB + Bonitur fix sprint.
 */

import { useState, useCallback } from 'react';
import { apiClient } from '../lib/apiClient';
import { useGeolocation } from './useGeolocation';

/**
 * @returns {{ gps, gpsLoading, gpsError, requestGps, createScoutingSession, activeAction, setActiveAction }}
 */
export function useQuickCreate() {
  const { position: gps, loading: gpsLoading, error: gpsError, requestLocation: requestGps } = useGeolocation();
  const [activeAction, setActiveAction] = useState(null); // 'note' | 'task' | 'scouting' | 'poi' | null

  /**
   * Create a new scouting session via POST /sessions.
   * Returns the session { id, started_at } or null on error.
   */
  const createScoutingSession = useCallback(async (fieldId = null) => {
    try {
      const payload = {};
      if (fieldId) payload.field_id = fieldId;
      if (gps) {
        payload.location_geojson = {
          type: 'Point',
          coordinates: [gps.longitude, gps.latitude],
        };
      }
      const res = await apiClient.post('/api/v1/aif/scouting/sessions', payload);
      return res?.data ?? res;
    } catch (e) {
      console.error('Failed to create scouting session:', e);
      return null;
    }
  }, [gps]);

  /**
   * Open a quick-create action. Requests GPS if not already available.
   */
  const openAction = useCallback((type) => {
    if (!gps && !gpsLoading) requestGps();
    setActiveAction(type);
  }, [gps, gpsLoading, requestGps]);

  const closeAction = useCallback(() => {
    setActiveAction(null);
  }, []);

  return {
    gps,
    gpsLoading,
    gpsError,
    requestGps,
    createScoutingSession,
    activeAction,
    openAction,
    closeAction,
  };
}
